/**
 * OASIS display vocabulary — every coded value's human label, in one place.
 *
 * WHY THIS EXISTS
 *
 * The application body stores coded values (`honorable_or_general`,
 * `mootCourtSelectee`, `top_10_percent`). Until now the only place those codes
 * became English was inside the applicant form's own templates and option
 * arrays, under `projects/oasis`. The internal admin app cannot import from
 * there, which is the real reason its detail page fell back to dumping JSON:
 * there was no vocabulary it could reach.
 *
 * PROVENANCE — the wording here is COPIED, not composed. Every label was lifted
 * from the committed source that owns it today so the two views cannot disagree
 * with the form the applicant filled in:
 *   - `sections/professional-accomplishments/*.component.html` (35 checklist labels)
 *   - `sections/honors-awards/*.component.html` (15 checklist labels)
 *   - `sections/education-experience/cards/card-options.ts`
 *   - `sections/citizenship/*.component.ts` (statutory criteria, verbatim)
 *   - `sections/military-service/*.component.ts`, `languages`, `references`
 *   - `sections/instructions/*.component.html` (the `<option>` labels)
 *   - `sections/demographics/demographics.options.ts`
 * The acknowledgement and certification statements are the exception: those are
 * multi-paragraph legal text on screen, so the labels here are the one-line
 * summaries already written as doc comments on `IAcknowledgements` and
 * `ICertifyAndSubmit`. A reviewer needs to know WHICH statement was affirmed,
 * not to re-read all seven.
 *
 * Phase 3 of this work points the form templates at these maps so each string
 * has exactly one home. Until then, treat this file as the copy of record for
 * the read-only views and keep the two in step if OARM revises wording.
 *
 * EVERY MAP IS A TOTAL `Record<Union, string>` ON PURPOSE. Adding a member to
 * one of the underlying unions then fails to compile here until it is given a
 * label, which is how a new enum value is prevented from reaching a user as a
 * raw code.
 */

import type {
	ClassRank,
	ClerkshipCourtType,
	ClerkshipJobTitle,
	DojFellowshipType,
	EpaPayment,
	EpaWorkSchedule,
	FellowshipType,
	GraduateLegalStatus,
	GraduationHonors,
	HowLearnedSource,
	IAcknowledgements,
	ICertifyAndSubmit,
	IHonorsAndAwards,
	ILitigationExperience,
	IOralAdvocacy,
	IProfessionalAccomplishments,
	IPublicService,
	IResearchAndWriting,
	LanguageProficiency,
	LawDegreePath,
	MilitaryBranch,
	MilitaryServiceStatus,
	NonCitizenEligibility,
	OasisDocType,
	PaymentType,
	ProgramType,
	ReferenceRelationship,
	ServiceCharacterization,
	SlipTrack,
	WorkSchedule,
	YesNo,
} from '../oasis-application.types';
import type { SlipPathwaysAttribute } from '../oasis-timeline.types';
import type { DemographicEthnicity, DemographicRace, DemographicSex, DisabilityCondition } from '../oasis-demographics.types';

// ─── Scalars ──────────────────────────────────────────────────────────────────

export const YES_NO_LABELS: Record<YesNo, string> = {
	yes: 'Yes',
	no: 'No',
};

export const PROGRAM_LABELS: Record<ProgramType, string> = {
	honors_program: 'Attorney General’s Honors Program',
	slip: 'Summer Law Intern Program',
};

export const SLIP_TRACK_LABELS: Record<SlipTrack, string> = {
	slip_pathways: 'SLIP — Pathways',
	slip_law_clerk: 'SLIP — Law Clerk',
};

/** From the Instructions screen's `<select>`, in the order it lists them. */
export const HOW_LEARNED_LABELS: Record<HowLearnedSource, string> = {
	oarm_webinar: 'OARM Webinar',
	doj_website: 'DOJ Website',
	law_school_career_services: 'Law School Career Services or Public Service Office',
	job_fair_or_recruitment_event: 'Job Fair or Recruitment Event',
	doj_attorney_or_employee: 'DOJ Attorney (including Assistant U.S. Attorneys) or Other DOJ Employee',
	judge: 'Judge',
};

// ─── Citizenship ──────────────────────────────────────────────────────────────

/**
 * Verbatim from the Citizenship screen, including the statutory citations. These
 * are legal criteria an applicant attested to, so they are reproduced in full
 * rather than paraphrased — a reviewer needs to see the exact basis claimed.
 */
export const NON_CITIZEN_ELIGIBILITY_LABELS: Record<NonCitizenEligibility, string> = {
	lpr_seeking_citizenship: 'Lawfully admitted for permanent residence and seeking citizenship as outlined in 8 U.S.C. 1324b(a)(3)(B).',
	refugee_declared_intent:
		'Admitted as a refugee (under 8 U.S.C. 1157) and has filed a declaration of intention to become a lawful permanent resident and then a citizen when eligible.',
	asylee_declared_intent:
		'Granted asylum (under 8 U.S.C. 1158) and has filed a declaration of intention to become a lawful permanent resident and then a citizen when eligible.',
	us_officer_or_employee_2009: 'Was an officer or employee of the United States government on December 16, 2009.',
	permanent_allegiance: 'Owes permanent allegiance to the United States (for example, as a native of American Samoa and Swains Island).',
	none: 'None of the criteria apply.',
};

// ─── Education ────────────────────────────────────────────────────────────────

export const LAW_DEGREE_PATH_LABELS: Record<LawDegreePath, string> = {
	us_jd: 'U.S. J.D.',
	non_us_jd: 'Non-U.S. J.D.',
	non_jd: 'Non-J.D. Legal Degree',
};

export const GRADUATION_HONORS_LABELS: Record<GraduationHonors, string> = {
	summa_cum_laude: 'Summa Cum Laude',
	magna_cum_laude: 'Magna Cum Laude',
	cum_laude: 'Cum Laude',
	with_distinction: 'With Distinction',
	none: 'None',
};

export const CLASS_RANK_LABELS: Record<ClassRank, string> = {
	top_5_percent: 'Top 5%',
	top_10_percent: 'Top 10%',
	top_20_percent: 'Top 20%',
	top_third: 'Top Third',
	top_half: 'Top Half',
	lower_half: 'Lower Half',
	does_not_rank: 'Law school does not rank',
	partially_ranks_not_ranked: 'Law school partially ranks; applicant is not a ranked student',
};

export const GRADUATE_LEGAL_STATUS_LABELS: Record<GraduateLegalStatus, string> = {
	joint_jd_llm_current_student:
		'Joint degree — current law student simultaneously enrolled in and earning credits for both a J.D. and an LLM (programs overlap; completion dates may differ).',
	graduate_llm_after_jd: 'Law school graduate — full-time LLM program that began after completing a J.D.',
	graduate_llm_after_non_jd: 'Law school graduate — full-time LLM program that began after completing a non-J.D. legal program.',
	graduate_non_llm_after_initial: 'Law school graduate — graduate legal program other than an LLM, begun after the initial legal degree.',
	graduate_part_time_llm_after_initial: 'Law school graduate — part-time LLM program that began after the initial legal degree.',
	none_of_the_above: 'None of the above.',
};

/** Undergraduate degree types, per the retired Other Education screen. */
export const UNDERGRAD_DEGREE_LABELS: Record<'BA' | 'BS' | 'other_baccalaureate', string> = {
	BA: 'B.A.',
	BS: 'B.S.',
	other_baccalaureate: 'Other Baccalaureate',
};

export const NON_LEGAL_GRAD_DEGREE_LABELS: Record<'MA' | 'MS' | 'MBA' | 'PhD' | 'other', string> = {
	MA: 'M.A.',
	MS: 'M.S.',
	MBA: 'M.B.A.',
	PhD: 'Ph.D.',
	other: 'Other',
};

/** SLIP-Pathways-only law school record attributes (check-all-that-apply). */
export const SLIP_PATHWAYS_ATTRIBUTE_LABELS: Record<SlipPathwaysAttribute, string> = {
	legal_writing_instructor: 'Selected as a legal writing instructor or teaching assistant',
	legal_writing_award: 'Received an award for legal writing (e.g., "best brief" in first-year moot court)',
	departmental_honors_thesis: 'Received departmental honors for a thesis or dissertation',
	law_review_writing_competition: 'Selected for Law Review / Journal by writing competition',
	law_review_grades_only: 'Selected for Law Review / Journal solely by grades',
	oral_advocacy_award: 'Received an award for oral advocacy in first-year moot court',
};

// ─── Eligibility-preserving activities ────────────────────────────────────────

export const EPA_WORK_SCHEDULE_LABELS: Record<EpaWorkSchedule, string> = {
	full_time: 'Full-time',
	part_time: 'Part-time',
};

export const EPA_PAYMENT_LABELS: Record<EpaPayment, string> = {
	paid: 'Paid',
	unpaid: 'Unpaid',
};

export const CLERKSHIP_COURT_TYPE_LABELS: Record<ClerkshipCourtType, string> = {
	us_supreme_court: 'U.S. Supreme Court',
	federal_circuit_court: 'Federal Circuit Court of Appeals',
	us_district_court: 'U.S. District Court',
	other_federal_court: 'Other Federal Court',
	state_or_tribal_court: 'State or Tribal Court',
	other_court: 'Other Court',
};

export const CLERKSHIP_JOB_TITLE_LABELS: Record<ClerkshipJobTitle, string> = {
	judicial_law_clerk: 'Judicial Law Clerk',
	staff_attorney: 'Staff Attorney',
	other: 'Other',
};

export const FELLOWSHIP_TYPE_LABELS: Record<FellowshipType, string> = {
	doj_fellowship: 'DOJ Fellowship or Two- to Three-Year HP Appointment',
	non_doj_honors_program: 'Non-DOJ Honors Program (State or Federal)',
	non_profit_public_interest: 'Non-Profit Public Service / Public Interest Fellowship',
	other_fellowship: 'Other Fellowship',
};

export const DOJ_FELLOWSHIP_LABELS: Record<DojFellowshipType, string> = {
	antitrust_aag: 'Antitrust Division AAG Fellowship',
	criminal_asset_forfeiture: 'Criminal Division Asset Forfeiture Fellowship',
	criminal_nsd_cyber: 'Criminal Division / NSD Cyber Fellowship',
	gaye_tenoso_indian_country: 'The Gaye L. Tenoso Indian Country Fellowship',
	eoir_term_honors: 'EOIR Term Honors Program Position',
	usao_eousa_term_honors: 'USAO or EOUSA Term Honors Program Position',
	other_term_honors: 'Other Term Honors Program Position',
};

// ─── Employment ───────────────────────────────────────────────────────────────

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
	paid: 'Paid',
	unpaid: 'Unpaid',
	volunteer: 'Volunteer',
};

export const WORK_SCHEDULE_LABELS: Record<WorkSchedule, string> = {
	full_time: 'Full-time',
	part_time: 'Part-time',
};

// ─── References ───────────────────────────────────────────────────────────────

export const REFERENCE_RELATIONSHIP_LABELS: Record<ReferenceRelationship, string> = {
	professor: 'Professor',
	employer: 'Employer',
	supervisor: 'Supervisor',
	colleague: 'Colleague',
	mentor: 'Mentor',
	other: 'Other',
};

// ─── Military service ─────────────────────────────────────────────────────────

/**
 * NOTE: `national_guard` is in the `MilitaryBranch` union but is NOT offered by
 * the current Military Service screen. It is labelled here because stored rows
 * from before the option list changed may still carry it, and a total `Record`
 * is what forces us to handle that rather than render the raw code.
 */
export const MILITARY_BRANCH_LABELS: Record<MilitaryBranch, string> = {
	army: 'Army',
	navy: 'Navy',
	air_force: 'Air Force',
	marine_corps: 'Marine Corps',
	space_force: 'Space Force',
	coast_guard: 'Coast Guard',
	national_guard: 'National Guard',
};

export const MILITARY_SERVICE_STATUS_LABELS: Record<MilitaryServiceStatus, string> = {
	served: 'Served in the armed forces',
	derivative_eligible: 'Claiming derivative preference',
	no_response_not_applicable: 'No response / not applicable',
};

export const SERVICE_CHARACTERIZATION_LABELS: Record<ServiceCharacterization, string> = {
	honorable_or_general: 'Honorable or General',
	other: 'Other',
};

// ─── Languages ────────────────────────────────────────────────────────────────

export const LANGUAGE_PROFICIENCY_LABELS: Record<LanguageProficiency, string> = {
	no_proficiency: 'No Proficiency',
	limited: 'Limited',
	general_professional: 'General Professional',
	advanced_professional: 'Advanced Professional',
	bilingual: 'Bilingual',
};

// ─── Supporting documents ─────────────────────────────────────────────────────

export const DOC_TYPE_LABELS: Record<OasisDocType, string> = {
	law_transcript: 'Law School Transcript',
	fellowship_offer_letter: 'Fellowship Offer Letter or Vacancy Announcement',
	dd214: 'DD-214',
	disability_preference: 'Disability Preference Documentation',
	derivative_preference: 'Derivative Preference Documentation',
};

// ─── Demographics ─────────────────────────────────────────────────────────────

export const DEMOGRAPHIC_SEX_LABELS: Record<DemographicSex, string> = {
	male: 'Male',
	female: 'Female',
	prefer_not_to_answer: 'Prefer not to answer',
};

export const DEMOGRAPHIC_ETHNICITY_LABELS: Record<DemographicEthnicity, string> = {
	not_hispanic_or_latino: 'Not Hispanic or Latino',
	hispanic_or_latino: 'Hispanic or Latino',
	prefer_not_to_answer: 'Prefer not to answer',
};

export const DEMOGRAPHIC_RACE_LABELS: Record<DemographicRace, string> = {
	american_indian_or_alaska_native: 'American Indian or Alaska Native',
	asian: 'Asian',
	black_or_african_american: 'Black or African American',
	native_hawaiian_or_other_pacific_islander: 'Native Hawaiian or Other Pacific Islander',
	white: 'White',
};

export const DISABILITY_CONDITION_LABELS: Record<DisabilityCondition, string> = {
	deaf_or_hard_of_hearing: 'Deaf or serious difficulty hearing',
	blind_or_low_vision: 'Blind or serious difficulty seeing even when wearing glasses',
	missing_extremities: 'Missing an arm, leg, hand, or foot',
	paralysis: 'Partial or complete paralysis (any cause)',
	significant_disfigurement: 'Significant disfigurement',
	significant_mobility_impairment: 'Significant mobility impairment',
	significant_psychiatric_disorder: 'Significant psychiatric disorder',
	intellectual_disability: 'Intellectual disability',
	developmental_disability: 'Developmental disability',
	traumatic_brain_injury: 'Traumatic brain injury',
	dwarfism: 'Dwarfism',
	epilepsy_or_seizure_disorder: 'Epilepsy or other seizure disorder',
	other_disability_or_condition: 'Other disability or serious health condition',
};

// ─── Acknowledgements & certifications ────────────────────────────────────────

/**
 * One-line summaries, not the on-screen statements.
 *
 * Each of these is a multi-paragraph legal statement on the Instructions screen.
 * Reproducing all seven in a read-only summary would bury the only thing the
 * reader is looking for — which statements were affirmed. The wording comes from
 * the doc comments on `IAcknowledgements`, which already summarize each one.
 */
export const ACKNOWLEDGEMENT_LABELS: Record<keyof IAcknowledgements, string> = {
	reviewedInformation: 'Reviewed and understands the instructions',
	consideredByComponents: 'Understands the application may be considered by other hiring offices',
	understandNoLateSubmissions: 'Understands late submissions are not accepted',
	transcriptRequired: 'Understands an acceptable law school transcript is required',
	continuousEligibility: 'Understands eligibility must be maintained throughout the hiring cycle',
	understandNoDeferral: 'Understands DOJ does not defer entry on duty absent extraordinary circumstances',
	certifyAccurate: 'Certifies the application is accurate and complete',
};

/** Same treatment as {@link ACKNOWLEDGEMENT_LABELS}; source is `ICertifyAndSubmit`. */
export const CERTIFY_AND_SUBMIT_LABELS: Record<keyof ICertifyAndSubmit, string> = {
	consentDisclosure: 'Consents to DOJ consulting and disclosing to authorities regarding discrepancies',
	understandPostSubmissionRules: 'Understands the application cannot be modified after submission without withdrawing and recertifying',
	noSimultaneousConsideration: 'Understands there is no simultaneous Honors Program and SLIP consideration',
	certifyTrueAndCorrect: 'Certifies the application is true and correct',
};

// ─── Professional accomplishments (check-all-that-apply) ──────────────────────

/** Group headings, matching the subheadings on the applicant screen. */
export const ACCOMPLISHMENT_GROUP_LABELS: Record<keyof IProfessionalAccomplishments, string> = {
	researchAndWriting: 'Research and Writing',
	oralAdvocacy: 'Oral Advocacy',
	publicService: 'Public Service',
	litigationExperience: 'Litigation Experience',
};

export const RESEARCH_AND_WRITING_LABELS: Record<keyof IResearchAndWriting, string> = {
	lawReviewEditorialPosition: 'Law Review/Journal editorial position',
	primaryLawReviewMember: 'Law School’s Primary Official Law Review/Journal member or selectee',
	otherLawReviewMember: 'Other School-Recognized Law Review/Journal member or selectee',
	researchAssistant: 'Research Assistant to college or law school professor or dean',
	fullTimeJournalist: 'Full-time employment as compensated journalist/article/column/news writer',
	compensatedSpeechWriting: 'Compensated speech writing',
	collegeNewspaperEditor: 'Editor on college student newspaper',
	fullTimeParalegal: 'Full-time employment as a paralegal',
};

export const ORAL_ADVOCACY_LABELS: Record<keyof IOralAdvocacy, string> = {
	mootCourtAwardWinner: 'Competitive Law School Moot Court (not required first year Moot Court) award winner',
	mootCourtParticipant: 'Competitive Law School Moot Court (not required first year Moot Court) participant/member',
	mootCourtSelectee: 'Competitive Law School Moot Court (not required first year Moot Court) selectee (i.e., have not yet actively participated).',
	mockTrialAwardWinner: 'Competitive Law School Mock Trial / Trial Advocacy team (not required first-year mock trial/trial advocacy) award winner',
	mockTrialMember:
		'Competitive Law School Mock Trial / Trial Advocacy team (not required first year mock trial/trial advocacy) member or participant',
	mockTrialSelectee:
		'Competitive Law School Mock Trial / Trial Advocacy team (not required first year mock trial/trial advocacy) selectee (i.e., have not yet actively participated).',
	collegeDebate: 'College debate team; college forensics team; individual college debate competition in college',
	publicSpeakingClub: 'Significant experience in non-legal public speaking club/group (e.g., Toastmasters)',
};

export const PUBLIC_SERVICE_LABELS: Record<keyof IPublicService, string> = {
	congressionalInternship: 'College or post-college congressional internship/externship',
	dojLegalInternship: 'U.S. Department of Justice legal internship/externship',
	federalLegalInternship: 'Federal government legal internship/externship (other than U.S. Department of Justice)',
	federalNonLegalInternship: 'Federal government non-legal internship/externship (including U.S. Department of Justice)',
	federalJudicialInternship: 'Federal judicial internship/externship',
	fullTimeFederalStateEmployee:
		'Full-time federal/state Government employee (other than in the armed services) for at least one continuous year, including full-time work in a congressional office or state legislature.',
	intergovernmentalOrgInternship:
		'Judicial or legal internship/externship/fellowship at an intergovernmental organization (e.g., the United Nations, International Criminal Court, World Trade Organization)',
	stateLocalGovtLegalInternship: 'State or local government legal internship/externship',
	vitaTaxPreparer: 'Tax Preparer in a Volunteer Income Tax Assistance (VITA) Program',
	legalNonProfitVolunteer:
		'Significant volunteer work with a legal non-profit, legal aid organization, or legal advocacy group (other than a law school clinic or student practice organization)',
	stateJudicialInternship: 'State or municipal judicial internship/externship',
	nonLegalNonProfitEmployment: 'Full or part-time employment, or significant volunteer work with a non-legal non-profit organization',
	nonLegalVolunteerism:
		'Significant non-legal volunteerism (excluding military service); e.g., Habitat for Humanity, Red Cross, animal rescue groups, etc.',
	peaceCorpsAmericorps: 'Peace Corps / Teach for America / AmeriCorps',
	proBonoWork: 'Pro-bono work',
	lawSchoolStudentBarOfficer: 'Law school student bar officer',
	correctionsLawEnforcementOfficer:
		'Experience as a corrections or law enforcement officer, or as an investigator or inspector general for a public agency',
};

export const LITIGATION_EXPERIENCE_LABELS: Record<keyof ILitigationExperience, string> = {
	lawSchoolPracticalExperience:
		'Successfully completed a litigation-related or client-service oriented law school practical experience (e.g., formal clinic or student practice organization) that provides opportunities to gain practical legal experience under the supervision of a licensed attorney',
	litigationRelatedWork:
		'Litigation-related experience (e.g., work in a prosecutor’s office, public defender’s office, or private law firm) where at least 2/3 of your time was assigned to litigation-related activities',
};

// ─── Honors & awards (check-all-that-apply) ───────────────────────────────────

export const HONORS_AND_AWARDS_LABELS: Record<keyof IHonorsAndAwards, string> = {
	lawSchoolDeansList: 'Law school Dean’s List',
	awardForLegalWriting: 'Award for legal writing',
	awardOralAdvocacyFirstYear: 'Award for oral advocacy, first year law school moot court',
	meritBasedLawScholarship: 'Academic merit-based law school scholarship (based on law school grades) recipient',
	highestGradeAward: 'Award for highest law school grade in a class (e.g., Am. Jur., book awards/prizes)',
	prestigiousLawSchoolHonors: 'Prestigious law school academic honors/prizes',
	prestigiousGradScholarship: 'Prestigious graduate school scholarship/fellowship',
	orderOfTheCoif: 'Order of the Coif',
	phiBetaKappa: 'Phi Beta Kappa',
	collegeSummaCumLaude: 'Graduated college summa cum laude or with highest honors',
	collegeMagnaCumLaude: 'Graduated college magna cum laude or with high honors',
	collegeCumLaude: 'Graduated college cum laude or with honors',
	lawSchoolSummaCumLaude: 'Graduated law school summa cum laude or with highest honors',
	lawSchoolMagnaCumLaude: 'Graduated law school magna cum laude or with high honors',
	lawSchoolCumLaude: 'Graduated law school cum laude or with honors',
};
